<template>
  <div class="underwriter-dropdown">
    <AdvancedDropdown
      v-model="modelValue"
      :options="options"
      :searchable="searchable"
      :can-add-new="false"
      :disabled="disabled"
      size="sm"
      multiple
    >
      <template v-slot:label>
        <span>Follower Underwriters</span>
      </template>
      <template v-slot:button-content>
        <template v-if="selectedOptions.length">
          {{ formattedSelectedText }}
        </template>
        <template v-else>Select Follower Underwriters</template>
      </template>

      <template v-slot:item-extra="slotData">
        <SyndicateLinesInput
          v-if="slotData && isSelected(slotData.value) && slotData.value !== KEEP_AS_IS_VALUE"
          :value="getSyndicates(slotData.value)"
          @input="(value) => onSyndicatesInput(value, slotData.value)"
        />
      </template>
    </AdvancedDropdown>
  </div>
</template>

<script>
import { defineComponent, computed } from '@vue/composition-api'

import { KEEP_AS_IS_OPTION, KEEP_AS_IS_VALUE } from '@constants'
import { useRLSGroup } from '@composables'
import { AdvancedDropdown } from '@components/advanced'
import { SyndicateLinesInput } from '../rls-syndicate-lines-input'
import { watchEffect } from '@vue/composition-api'

export default defineComponent({
  name: 'RLSUnderwriterFollowersChecklist',
  components: {
    SyndicateLinesInput,
    AdvancedDropdown
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    isFetching: {
      type: Boolean,
      default: false
    },
    rlsGroupItems: {
      type: Array,
      default: () => []
    },
    autoFetchRlsGroupItems: {
      type: Boolean,
      default: true
    },
    searchable: {
      type: Boolean,
      default: true
    },
    useKeepAsIsOption: Boolean,
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['input'],
  setup(props, { emit }) {
    const { isFetching: innerFetching, rlsGroupItems } = useRLSGroup(
      'Underwriter',
      props.autoFetchRlsGroupItems
    )

    const modelValue = computed({
      get: () => props.value.map((item) => item.id),
      set: (newValue) => {
        // Same underwriter may already be selected as lead (a market leading
        // on one syndicate, following on another) - carry its syndicate
        // lines over rather than discarding them, since changing which
        // underwriters are selected here is not an edit to what either
        // bucket's syndicates already hold.
        const validValue = newValue
          .map((id) => {
            const existingItem = props.value.find((item) => item.id === id)
            return { id, syndicates: existingItem ? existingItem.syndicates : [] }
          })
          .filter((item) => !!item.id)

        emit('input', validValue)
      }
    })

    // Every company type can see every underwriter here - access is
    // controlled by disabling the whole control once the record already has
    // a Lead Underwriter (see BDXEditForm's isLeadUnderwriterDisabled /
    // isFollowUnderwriterAssignmentDisabled), not by filtering who appears
    // in this list.
    //
    // Also no longer filtered by disabledValues: the same underwriter can
    // now be selected here AND as the lead at once (leading on one
    // syndicate, following on another) - see GlobalUMRUnderwriter's own
    // docstring on the backend for why this is a real business case, not a
    // data error.
    const options = computed(() => {
      let options = rlsGroupItems.value;
      if (props.useKeepAsIsOption) {
        options = [{
          id: KEEP_AS_IS_VALUE,
          underwriter: KEEP_AS_IS_OPTION.text
        }, ...options];
      }

      options = options
        .map((rlsGroupItem) => {
          const item = props.value.find((item) => item.id === rlsGroupItem.id)
          const syndicates = (item && item.syndicates) || []
          const total = syndicates.reduce((sum, s) => {
            const pct = Number(s.slip_share_pct)
            return sum + (Number.isFinite(pct) ? pct : 0)
          }, 0)

          let text = rlsGroupItem.underwriter
          if (total > 0) {
            // Floating-point addition of two 9dp values can land a few
            // units past the 9th decimal (e.g. 60.123456789 + 39.87654321
            // -> 99.99999999900001) - round to the precision the rest of
            // the platform shows before display, not raw IEEE754 noise.
            // Trailing zeros then come off, same as everywhere else a share
            // is shown - a plain 40% share reads 40%, not 40.000000000%.
            text += ` (${total.toFixed(9).replace(/\.?0+$/, '')}%)`
          }

          return {
            text: text,
            value: rlsGroupItem.id
          }
        })
      return options;
    })

    const selectedOptions = computed(() =>
      options.value.filter((opt) => modelValue.value.includes(opt.value))
    )

    const formattedSelectedText = computed(() => {
      const selected = selectedOptions.value
      if (selected.length === 0) return ''

      if (selected.length <= 2) {
        return selected.map((opt) => opt.text).join(', ')
      }

      const firstTwo = selected
        .slice(0, 2)
        .map((opt) => opt.text)
        .join(', ')
      return `${firstTwo} and ${selected.length - 2} more`
    })

    const isSelected = (value) => {
      return modelValue.value.includes(value)
    }

    const getSyndicates = (id) => {
      const item = props.value.find((item) => item.id === id)
      return (item && item.syndicates) || []
    }

    const onSyndicatesInput = (syndicates, id) => {
      const newValue = props.value.map((item) => {
        if (item.id === id) {
          return { id, syndicates }
        }
        return item
      })
      emit('input', newValue)
    }

    watchEffect(() => {
      if (!props.autoFetchRlsGroupItems) {
        innerFetching.value = props.isFetching
        rlsGroupItems.value = props.rlsGroupItems
      }
    })

    return {
      modelValue,
      options,
      selectedOptions,
      formattedSelectedText,
      isSelected,
      getSyndicates,
      onSyndicatesInput,
      innerFetching,
      KEEP_AS_IS_VALUE,
    }
  }
})
</script>

<style scoped>
.underwriter-dropdown {
  position: relative;
}

.underwriter-dropdown >>> .advanced-dropdown .dropdown-menu .list-group-item {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  min-height: 36px;
}

.underwriter-dropdown >>> .advanced-dropdown .dropdown-menu {
  width: min(430px, calc(100vw - 32px));
}

.underwriter-dropdown
  >>> .advanced-dropdown
  .dropdown-menu
  .advanced-dropdown-item-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.underwriter-dropdown
  >>> .advanced-dropdown
  .dropdown-menu
  .list-group-item
  .item-extra {
  flex: 0 0 auto;
  margin-left: 12px;
}

.underwriter-dropdown >>> .advanced-dropdown .dropdown-toggle {
  max-width: 337px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
